import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { CheckCircle2, AlertTriangle, Trash2, FolderTree } from 'lucide-react';

const ProjectEditDialog = ({ open, onOpenChange, project, onSuccess, onDelete }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    start_date: '',
    end_date: '',
    planned_cost: '',
    hourly_rate: '',
    include_time_costs: true,
    department_id: ''
  });
  const [showCompleteConfirm, setShowCompleteConfirm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [departments, setDepartments] = useState([]);

  useEffect(() => {
    if (open) {
      fetchDepartments();
    }
  }, [open]);

  useEffect(() => {
    if (project && open) {
      setFormData({
        name: project.name,
        description: project.description,
        start_date: project.start_date,
        end_date: project.end_date,
        planned_cost: project.planned_cost.toString(),
        hourly_rate: (project.hourly_rate || 120).toString(),
        include_time_costs: project.include_time_costs !== false,
        department_id: project.department_id || ''
      });
      setShowCompleteConfirm(false);
      setShowDeleteConfirm(false);
    }
  }, [project, open]);

  const fetchDepartments = async () => {
    try {
      const response = await axios.get(`${API}/departments`);
      setDepartments(response.data);
    } catch (error) {
      console.error('Failed to fetch departments', error);
    }
  };

  const handleDeleteProject = async () => {
    try {
      await axios.delete(`${API}/projects/${project.id}`);
      toast.success('Projekt erfolgreich gelöscht!');
      onOpenChange(false);
      if (onDelete) {
        onDelete();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Löschen des Projekts');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const updateData = {
        name: formData.name,
        description: formData.description,
        start_date: formData.start_date,
        end_date: formData.end_date,
        planned_cost: parseFloat(formData.planned_cost),
        hourly_rate: parseFloat(formData.hourly_rate),
        include_time_costs: formData.include_time_costs,
        department_id: formData.department_id || null
      };

      await axios.put(`${API}/projects/${project.id}`, updateData);
      toast.success('Projekt erfolgreich aktualisiert!');
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Aktualisieren des Projekts');
    }
  };

  const handleCompleteProject = async () => {
    try {
      await axios.put(`${API}/projects/${project.id}`, { status: 'completed' });
      toast.success('Projekt abgeschlossen!');
      onOpenChange(false);
      onSuccess();
    } catch (error) {
      toast.error('Fehler beim Abschließen des Projekts');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="edit-project-dialog">
        <DialogHeader>
          <DialogTitle>Projekt bearbeiten</DialogTitle>
          <DialogDescription>Ändern Sie die Projekt-Details</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="project-name">Projektname</Label>
            <Input
              id="project-name"
              data-testid="edit-project-name-input"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="project-description">Beschreibung</Label>
            <Textarea
              id="project-description"
              data-testid="edit-project-description-input"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              required
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="project-start-date">Startdatum</Label>
              <Input
                id="project-start-date"
                type="date"
                data-testid="edit-project-start-date-input"
                value={formData.start_date}
                onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="project-end-date">Enddatum</Label>
              <Input
                id="project-end-date"
                type="date"
                data-testid="edit-project-end-date-input"
                value={formData.end_date}
                onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                required
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="project-planned-cost">Geplante Kosten (CHF)</Label>
              <Input
                id="project-planned-cost"
                type="number"
                step="0.01"
                data-testid="edit-project-planned-cost-input"
                value={formData.planned_cost}
                onChange={(e) => setFormData({ ...formData, planned_cost: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="project-hourly-rate">Stundensatz (CHF/h)</Label>
              <Input
                id="project-hourly-rate"
                type="number"
                step="0.01"
                min="0"
                data-testid="edit-project-hourly-rate-input"
                value={formData.hourly_rate}
                onChange={(e) => setFormData({ ...formData, hourly_rate: e.target.value })}
                required
              />
            </div>
          </div>
          
          {/* Toggle for including time costs */}
          <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
            <div className="space-y-0.5">
              <Label htmlFor="include-time-costs" className="text-sm font-medium">
                Zeitkosten in Total einbeziehen
              </Label>
              <p className="text-xs text-muted-foreground">
                Wenn aktiviert, werden erfasste Stunden automatisch in die effektiven Kosten einberechnet.
              </p>
            </div>
            <Switch
              id="include-time-costs"
              checked={formData.include_time_costs}
              onCheckedChange={(checked) => setFormData({ ...formData, include_time_costs: checked })}
            />
          </div>
          
          {/* Department Selection */}
          {departments.length > 0 && (
            <div className="space-y-2">
              <Label htmlFor="department">Abteilung</Label>
              <Select 
                value={formData.department_id || 'none'} 
                onValueChange={(value) => setFormData({ ...formData, department_id: value === 'none' ? '' : value })}
              >
                <SelectTrigger data-testid="edit-project-department-select">
                  <SelectValue placeholder="Abteilung auswählen (optional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Keine Abteilung</SelectItem>
                  {departments.map((dept) => (
                    <SelectItem key={dept.id} value={dept.id}>
                      <div className="flex items-center gap-2">
                        <FolderTree className="w-4 h-4" />
                        {dept.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Weisen Sie das Projekt einer Abteilung zu
              </p>
            </div>
          )}
          
          <Button type="submit" className="w-full" data-testid="edit-project-submit-button">
            Projekt aktualisieren
          </Button>
          
          {/* Complete Project Section */}
          {project?.status === 'active' && (
            <div className="pt-4 border-t">
              {!showCompleteConfirm ? (
                <Button 
                  type="button"
                  variant="outline" 
                  className="w-full" 
                  onClick={() => setShowCompleteConfirm(true)}
                  data-testid="complete-project-button"
                >
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  Projekt abschließen
                </Button>
              ) : (
                <div className="space-y-3 p-3 bg-amber-50 border border-amber-200 rounded-lg dark:bg-amber-950/30 dark:border-amber-800">
                  <div className="flex items-center gap-2 text-amber-700 dark:text-amber-400">
                    <AlertTriangle className="w-4 h-4" />
                    <span className="text-sm font-medium">Projekt wirklich abschließen?</span>
                  </div>
                  <p className="text-xs text-amber-600 dark:text-amber-500">
                    Ein abgeschlossenes Projekt kann nicht mehr bearbeitet werden.
                  </p>
                  <div className="flex gap-2">
                    <Button 
                      type="button"
                      variant="outline" 
                      size="sm"
                      className="flex-1"
                      onClick={() => setShowCompleteConfirm(false)}
                    >
                      Abbrechen
                    </Button>
                    <Button 
                      type="button"
                      size="sm"
                      className="flex-1 bg-amber-600 hover:bg-amber-700"
                      onClick={handleCompleteProject}
                      data-testid="confirm-complete-project-button"
                    >
                      Ja, abschließen
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
          
          {/* Delete Project Section */}
          <div className="pt-4 border-t">
            {!showDeleteConfirm ? (
              <Button 
                type="button"
                variant="outline" 
                className="w-full text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700 dark:border-red-800 dark:hover:bg-red-950/30" 
                onClick={() => setShowDeleteConfirm(true)}
                data-testid="delete-project-button"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Projekt löschen
              </Button>
            ) : (
              <div className="space-y-3 p-3 bg-red-50 border border-red-200 rounded-lg dark:bg-red-950/30 dark:border-red-800">
                <div className="flex items-center gap-2 text-red-700 dark:text-red-400">
                  <AlertTriangle className="w-4 h-4" />
                  <span className="text-sm font-medium">Projekt wirklich löschen?</span>
                </div>
                <p className="text-xs text-red-600 dark:text-red-500">
                  Das Projekt und alle zugehörigen Tasks werden unwiderruflich gelöscht!
                </p>
                <div className="flex gap-2">
                  <Button 
                    type="button"
                    variant="outline" 
                    size="sm"
                    className="flex-1"
                    onClick={() => setShowDeleteConfirm(false)}
                  >
                    Abbrechen
                  </Button>
                  <Button 
                    type="button"
                    size="sm"
                    variant="destructive"
                    className="flex-1"
                    onClick={handleDeleteProject}
                    data-testid="confirm-delete-project-button"
                  >
                    Ja, löschen
                  </Button>
                </div>
              </div>
            )}
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ProjectEditDialog;
